import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErreurApi, api } from "./client";

// ---------------------------------------------------------------------------
// Formes renvoyées par l'API. Recopiées à la main plutôt qu'importées de
// `@delsa/shared` : ces types décrivent le contrat HTTP (ce qui sort de
// `vueBuild`, `vueMedia`…), pas les lignes SQLite qui les alimentent.
// ---------------------------------------------------------------------------

export type Role = "admin" | "editeur";

export type Utilisateur = {
  id: number;
  email: string;
  nom: string;
  role: Role;
  createdAt: string;
  lastLogin: string | null;
};

export type StatutBuild = "en_cours" | "succes" | "echec";

export type Build = {
  id: number;
  demarreA: string;
  termineA: string | null;
  statut: StatutBuild;
  userId: number | null;
};

export type EtatPublication = {
  /** Identifiant du build en cours, `null` si aucun. Pas un booléen : l'API
   *  renvoie l'identifiant, dont l'écran a besoin pour suivre le bon build. */
  enCours: number | null;
  builds: Build[];
};

export type Media = {
  id: number;
  chantierId: number | null;
  chemin: string;
  url: string;
  urlMiniature: string;
  type: string;
  alt: string;
  legende: string;
  largeur: number;
  hauteur: number;
  poids: number;
  ordre: number;
  createdAt: string;
};

/** Clés de cache regroupées ici : une faute de frappe éparpillée ne se voit pas. */
export const cles = {
  session: ["session"] as const,
  publication: ["publication"] as const,
  medias: ["medias"] as const,
};

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

/**
 * Vérifie la session auprès de `GET /api/auth/me`.
 *
 * Un 401 est ici une réponse légitime — « personne n'est connecté » — et non
 * une panne : il se traduit par `utilisateur: null`, sans redirection (sinon
 * l'écran de connexion se renverrait indéfiniment vers lui-même) et sans
 * réessai (React Query rejouerait trois fois une requête dont la réponse est
 * déjà connue).
 */
export function useSession() {
  return useQuery({
    queryKey: cles.session,
    queryFn: async (): Promise<Utilisateur | null> => {
      try {
        const { utilisateur } = await api.get<{ utilisateur: Utilisateur }>("/api/auth/me", {
          toleranteAuxAnonymes: true,
        });
        return utilisateur;
      } catch (erreur) {
        if (erreur instanceof ErreurApi && erreur.statut === 401) return null;
        throw erreur;
      }
    },
    retry: false,
    // La session glisse à chaque requête côté API : inutile de la resonder
    // à chaque remontage d'un composant.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export type IdentifiantsConnexion = {
  email: string;
  motDePasse: string;
};

export function useConnexion() {
  const cache = useQueryClient();

  return useMutation<Utilisateur, ErreurApi, IdentifiantsConnexion>({
    mutationFn: async (identifiants) => {
      const { utilisateur } = await api.post<{ utilisateur: Utilisateur }>(
        "/api/auth/login",
        identifiants,
      );
      return utilisateur;
    },
    onSuccess: (utilisateur) => {
      // Écrit directement dans le cache : la réponse de connexion contient
      // déjà l'utilisateur, un aller-retour vers /me n'apprendrait rien.
      cache.setQueryData(cles.session, utilisateur);
    },
  });
}

export function useDeconnexion() {
  const cache = useQueryClient();

  return useMutation<void, ErreurApi, void>({
    mutationFn: async () => {
      await api.post<{ ok: boolean }>("/api/auth/logout");
    },
    onSuccess: () => {
      // Vider entièrement le cache, pas seulement la session : les leads et
      // les médias déjà chargés ne doivent pas rester lisibles après coup.
      cache.clear();
      cache.setQueryData(cles.session, null);
    },
  });
}

// ---------------------------------------------------------------------------
// Publication
// ---------------------------------------------------------------------------

export function usePublication() {
  return useQuery({
    queryKey: cles.publication,
    queryFn: () => api.get<EtatPublication>("/api/publication"),
    // Le flux SSE `/api/publication/flux` prendra le relais à la tâche
    // dédiée ; ici un simple sondage suffit à afficher le dernier build.
    refetchInterval: (requete) => (requete.state.data?.enCours ? 5_000 : false),
  });
}

// ---------------------------------------------------------------------------
// Médiathèque
// ---------------------------------------------------------------------------

export function useMedias() {
  return useQuery({
    queryKey: cles.medias,
    queryFn: () => api.get<{ medias: Media[] }>("/api/medias"),
  });
}
